/**
 * Creation de compte par le client (Franck, 07/10/2026) : « le nom est choisi
 * par le client a la creation et il pourra le modifier par la suite ; l'id
 * est un numero croissant que personne peut modifier ».
 *
 * Deux etapes, deux adresses :
 *   /inscription                 : le formulaire ; Sign365 envoie un lien ;
 *   /inscription/confirmer?token : le choix du mot de passe ; le compte est
 *                                  cree et son numero affiche.
 */
import { useEffect, useState } from "react";

import { ApiError } from "../api";
import { session } from "../session";
import { Erreur } from "../components/ui";

const LONGUEUR_MINIMALE = 12;   // comme le serveur (ConfirmationInscription)

async function poster(chemin: string, corps: unknown): Promise<unknown> {
  const reponse = await fetch(`/api${chemin}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corps),
  });
  let json: { detail?: unknown } | null = null;
  try {
    json = await reponse.json();
  } catch {
    /* corps non-JSON */
  }
  if (!reponse.ok) {
    const detail = typeof json?.detail === "string" ? json.detail : null;
    throw new ApiError(reponse.status === 429 ? "rate_limited" : "validation", reponse.status, detail);
  }
  return json;
}

function Demande({ onRetour }: { onRetour: () => void }) {
  // 08/10/2026 (Franck) : l'e-mail seulement ; la fiche client (particulier
  // ou societe) est demandee a la premiere recharge.
  const [email, setEmail] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [envoye, setEnvoye] = useState(false);

  if (envoye) {
    return (
      <div className="stack">
        <h1 className="card__title">Vérifiez votre messagerie</h1>
        <p>
          Un e-mail vient de partir vers <strong>{email}</strong>. Ouvrez le lien qu'il contient pour choisir votre
          mot de passe : votre compte sera créé à ce moment-là. Le lien est valable 24 heures.
        </p>
        <button type="button" className="btn btn--secondary" onClick={onRetour}>Retour à la connexion</button>
      </div>
    );
  }

  return (
    <form className="stack" onSubmit={(e) => {
      e.preventDefault();
      setEnvoi(true);
      setErreur(null);
      poster("/auth/inscription", { email: email.trim() })
        .then(() => setEnvoye(true), setErreur)
        .finally(() => setEnvoi(false));
    }}>
      <div>
        <h1 className="card__title" style={{ marginBottom: "0.15rem" }}>Créer un compte</h1>
        <p className="card__lead" style={{ marginBottom: 0 }}>Sign365.io : signer, sécuriser, prouver.</p>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="ins-email">Adresse e-mail</label>
        <input id="ins-email" className="input" type="email" required autoComplete="email"
               value={email} onChange={(e) => setEmail(e.target.value)} />
        <p className="muted" style={{ marginTop: "0.3rem" }}>
          Vos coordonnées de facturation vous seront demandées à votre première recharge.
        </p>
      </div>
      <Erreur erreur={erreur} />
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={envoi || !email.includes("@")}>
          {envoi ? "Envoi…" : "Créer mon compte"}
        </button>
        <button type="button" className="btn btn--link" onClick={onRetour}>J'ai déjà un compte</button>
      </div>
    </form>
  );
}

function Confirmation({ onTermine }: { onTermine: () => void }) {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const [compte, setCompte] = useState<{ numero: number; nom: string; email: string } | null>(null);
  const [demande, setDemande] = useState<{ email: string; societe: string } | null>(null);
  const pret = motDePasse.length >= LONGUEUR_MINIMALE && motDePasse === confirmation;

  // L'adresse remplit le champ « nom d'utilisateur » du formulaire : sans
  // lui, Chrome proposait d'enregistrer le mot de passe sans l'e-mail
  // (Franck, 07/10/2026).
  useEffect(() => {
    fetch(`/api/auth/inscription/confirmer?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new ApiError(r.status === 429 ? "rate_limited" : "not_found", r.status,
                                      (await r.json().catch(() => null))?.detail ?? null);
        setDemande(await r.json());
      })
      .catch(setErreur);
  }, [token]);

  if (compte) {
    return (
      <div className="stack">
        <h1 className="card__title">Votre compte est créé</h1>
        <p>
          Numéro de compte <strong>{compte.numero}</strong> (utile pour le support). Connectez-vous avec votre
          e-mail et votre mot de passe.
        </p>
        <button type="button" className="btn btn--primary" onClick={() => {
          session.memoriserOrganisation(String(compte.numero));
          onTermine();
        }}>Se connecter</button>
      </div>
    );
  }

  return (
    <form className="stack" onSubmit={(e) => {
      e.preventDefault();
      setEnvoi(true);
      setErreur(null);
      poster("/auth/inscription/confirmer", { token, password: motDePasse })
        .then((r) => setCompte(r as { numero: number; nom: string; email: string }), setErreur)
        .finally(() => setEnvoi(false));
    }}>
      <h1 className="card__title">Choisissez votre mot de passe</h1>
      {demande && (
        <p style={{ margin: 0 }}>Compte pour <strong>{demande.email}</strong>.</p>
      )}
      <input type="email" name="email" autoComplete="username" value={demande?.email ?? ""} readOnly
             hidden aria-hidden="true" />
      <div className="field">
        <label className="field-label" htmlFor="ins-mdp">Mot de passe ({LONGUEUR_MINIMALE} caractères au moins)</label>
        <input id="ins-mdp" className="input" type="password" autoComplete="new-password"
               value={motDePasse} onChange={(e) => setMotDePasse(e.target.value)} />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="ins-mdp2">Confirmation</label>
        <input id="ins-mdp2" className="input" type="password" autoComplete="new-password"
               value={confirmation} onChange={(e) => setConfirmation(e.target.value)} />
        {confirmation && motDePasse !== confirmation && (
          <p className="muted" role="alert" style={{ marginTop: "0.3rem" }}>Les deux saisies diffèrent.</p>
        )}
      </div>
      <Erreur erreur={erreur} />
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={!pret || envoi}>
          {envoi ? "Création…" : "Créer mon compte"}
        </button>
      </div>
    </form>
  );
}

export function Inscription({ onTermine }: { onTermine: () => void }) {
  const confirmer = window.location.pathname.startsWith("/inscription/confirmer");
  return (
    <div className="centered" style={{ padding: "2rem 1rem" }}>
      <div className="card" style={{ width: "min(30rem, 100%)" }}>
        {confirmer ? <Confirmation onTermine={onTermine} /> : <Demande onRetour={onTermine} />}
      </div>
    </div>
  );
}
